import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/custom/SearchableSelect";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FolderOpen, Search, Plus, Loader2, Trash2, X, Users } from "lucide-react";
import { fetchWithAuth, api, Account } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";
import { format } from "date-fns";

interface ClientGroup {
  id: string;
  name: string;
  description: string | null;
  owner_user_id: string | null;
  owner: { id: string; name: string; email: string } | null;
  client_count: number;
  created_at: string;
  updated_at: string;
}

interface GroupMember {
  account_id: string;
  created_at: string;
  accounts: { id: string; account_name: string } | null;
}

interface GroupDetail extends ClientGroup {
  members: GroupMember[];
}

export default function ClientGroups() {
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupDetail | null>(null);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const queryClient = useQueryClient();

  const { data: groups = [], isLoading } = useQuery<ClientGroup[]>({
    queryKey: ["client-groups", searchQuery],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      return fetchWithAuth<ClientGroup[]>(`/api/client-groups${params.toString() ? `?${params}` : ""}`);
    },
  });

  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ["accounts"],
    queryFn: () => api.getAccounts(),
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description: string }) => {
      return fetchWithAuth<ClientGroup>("/api/client-groups", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-groups"] });
      toast({ title: "Group created" });
      setModalOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name: string; description: string } }) => {
      return fetchWithAuth<ClientGroup>(`/api/client-groups/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-groups"] });
      toast({ title: "Group updated" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await fetchWithAuth(`/api/client-groups/${id}`, { method: "DELETE" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-groups"] });
      toast({ title: "Group deleted" });
      setModalOpen(false);
      setEditingGroup(null);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await fetchWithAuth("/api/client-groups/bulk-delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-groups"] });
      toast({ title: `${selectedIds.length} groups deleted` });
      setSelectedIds([]);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: async ({ groupId, accountId }: { groupId: string; accountId: string }) => {
      return fetchWithAuth<GroupMember>(`/api/client-groups/${groupId}/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: accountId }),
      });
    },
    onSuccess: (data) => {
      if (editingGroup) {
        setEditingGroup({
          ...editingGroup,
          members: [...editingGroup.members, data],
          client_count: editingGroup.client_count + 1,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["client-groups"] });
      toast({ title: "Client added to group" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async ({ groupId, accountId }: { groupId: string; accountId: string }) => {
      await fetchWithAuth(`/api/client-groups/${groupId}/members/${accountId}`, { method: "DELETE" });
      return accountId;
    },
    onSuccess: (accountId) => {
      if (editingGroup) {
        setEditingGroup({
          ...editingGroup,
          members: editingGroup.members.filter((m) => m.account_id !== accountId),
          client_count: Math.max(0, editingGroup.client_count - 1),
        });
      }
      queryClient.invalidateQueries({ queryKey: ["client-groups"] });
      toast({ title: "Client removed from group" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    setFormData({ name: "", description: "" });
    setEditingGroup(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = async (group: ClientGroup) => {
    try {
      const detail = await fetchWithAuth<GroupDetail>(`/api/client-groups/${group.id}`);
      setEditingGroup(detail);
      setFormData({ name: detail.name, description: detail.description || "" });
      setModalOpen(true);
    } catch (error: any) {
      toast({ title: "Error loading group", description: error.message, variant: "destructive" });
    }
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      toast({ title: "Group name is required", variant: "destructive" });
      return;
    }

    if (editingGroup) {
      updateMutation.mutate({ id: editingGroup.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = async () => {
    if (!editingGroup) return;
    if (await askConfirm({
      title: "Delete this client group?",
      subject: "client group",
      name: editingGroup.name,
      body: "will be deleted.",
      lines: ["Its client accounts are not deleted."],
      confirmLabel: "Delete",
      destructive: true,
    })) {
      deleteMutation.mutate(editingGroup.id);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (await askConfirm({
      title: "Delete the selected client groups?",
      subject: "client group",
      name: `${selectedIds.length} client group${selectedIds.length === 1 ? "" : "s"}`,
      body: "will be deleted.",
      lines: ["Their client accounts are not deleted."],
      confirmLabel: "Delete",
      destructive: true,
    })) {
      bulkDeleteMutation.mutate(selectedIds);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === groups.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(groups.map((g) => g.id));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const availableAccounts = useMemo(() => {
    if (!editingGroup) return [];
    const memberIds = new Set(editingGroup.members.map((m) => m.account_id));
    return accounts.filter((a) => !memberIds.has(a.id) && a.is_active);
  }, [accounts, editingGroup]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FolderOpen className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Client Groups</h1>
              <p className="text-muted-foreground">Organize clients into groups</p>
            </div>
          </div>
          <Button onClick={openCreateModal} data-testid="button-new-group">
            <Plus className="w-4 h-4 mr-2" />
            New Group
          </Button>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search groups..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="input-search-groups"
                />
              </div>
              {selectedIds.length > 0 && (
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteMutation.isPending}
                  data-testid="button-bulk-delete"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete ({selectedIds.length})
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : groups.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No client groups found</p>
                <Button variant="link" onClick={openCreateModal}>
                  Create your first group
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedIds.length === groups.length && groups.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="checkbox-select-all"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="text-center">Clients</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((group) => (
                    <TableRow
                      key={group.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => openEditModal(group)}
                      data-testid={`row-group-${group.id}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(group.id)}
                          onCheckedChange={() => toggleSelect(group.id)}
                          data-testid={`checkbox-group-${group.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{group.name}</TableCell>
                      <TableCell className="text-muted-foreground max-w-xs truncate">
                        {group.description || "—"}
                      </TableCell>
                      <TableCell>{group.owner?.name || group.owner?.email || "—"}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{group.client_count}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(group.created_at), "MMM d, yyyy")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingGroup ? "Edit Group" : "Create Group"}</DialogTitle>
              <DialogDescription>
                {editingGroup
                  ? "Update group details and manage members"
                  : "Create a new client group to organize your clients"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="group-name">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="group-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter group name"
                  data-testid="input-group-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="group-description">Description</Label>
                <Textarea
                  id="group-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={2}
                  data-testid="input-group-description"
                />
              </div>

              {editingGroup && (
                <>
                  <Separator className="my-4" />
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <Label>Members</Label>
                        <Badge variant="outline">{editingGroup.members.length}</Badge>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <SearchableSelect
                        value=""
                        onValueChange={(accountId) => {
                          if (accountId) {
                            addMemberMutation.mutate({ groupId: editingGroup.id, accountId });
                          }
                        }}
                        options={availableAccounts.map((a) => ({ value: a.id, label: a.account_name }))}
                        placeholder="Add a client..."
                        searchPlaceholder="Search clients..."
                        emptyText="No clients available"
                        className="flex-1"
                        data-testid="select-add-client"
                      />
                    </div>

                    {editingGroup.members.length > 0 ? (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {editingGroup.members.map((member) => (
                          <div
                            key={member.account_id}
                            className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                          >
                            <span className="text-sm font-medium">
                              {member.accounts?.account_name || "Unknown"}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                removeMemberMutation.mutate({
                                  groupId: editingGroup.id,
                                  accountId: member.account_id,
                                })
                              }
                              disabled={removeMemberMutation.isPending}
                              data-testid={`button-remove-member-${member.account_id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        No clients in this group yet
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            <DialogFooter className="flex justify-between">
              <div>
                {editingGroup && (
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    data-testid="button-delete-group"
                  >
                    {deleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving} data-testid="button-save-group">
                  {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingGroup ? "Save Changes" : "Create Group"}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
